import React, { useState, useEffect } from 'react';
import { 
  Save, X, DollarSign, Users, ArrowUp, Info
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast, ToastContainer } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import PreContractService from '../../services/preContractService';

const CreateDoA = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const { success, error: showError, toasts, removeToast } = useToast();
  
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    role: '',
    approvalLimit: '',
    currency: 'USD',
    rank: 1,
    isActive: true
  });

  useEffect(() => {
    const fetchRule = async () => {
      if (!isEditMode) return;
      setIsLoading(true);
      try {
        const response = await PreContractService.getDoA(id);
        if (response.data) {
          setFormData({
            role: response.data.role || '',
            approvalLimit: response.data.approvalLimit || '',
            currency: response.data.currency || 'USD',
            rank: response.data.rank || 1,
            isActive: response.data.isActive ?? true
          });
        }
      } catch (err) {
        console.error(err);
        showError("Failed to load rule details.");
        navigate('/contracts/pre-contract/doa');
      } finally {
        setIsLoading(false);
      }
    };
    fetchRule();
  }, [id, isEditMode, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.role || !formData.approvalLimit || !formData.rank) {
      showError("Please fill in all required fields.");
      return;
    }

    setIsSaving(true);
    try {
      if (isEditMode) {
        await PreContractService.updateDoA(id, formData);
        success("DoA rule updated successfully");
      } else {
        await PreContractService.createDoA(formData);
        success("DoA rule created successfully");
      }
      setTimeout(() => {
        setIsSaving(false);
        navigate('/contracts/pre-contract/doa');
      }, 1000);
    } catch (err) {
      console.error(err);
      showError(err.response?.data?.message || "Failed to save rule.");
      setIsSaving(false);
    }
  };

  const headerActions = [
    { label: 'Cancel', icon: <X size={18} />, onClick: () => navigate('/contracts/pre-contract/doa'), variant: 'outline' },
    { label: isSaving ? 'Saving...' : (isEditMode ? 'Update Rule' : 'Save Rule'), icon: <Save size={18} />, onClick: handleSave, variant: 'primary', disabled: isSaving || isLoading }
  ];

  if (isLoading) return <div className="p-5 text-center">Loading...</div>;

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title={isEditMode ? "Edit DoA Rule" : "Create DoA Rule"}
        description="Configure authority limits for a specific role."
        actions={headerActions}
      />

      <div className="page-container">
        <div className="row">
          <div className="col-lg-8">
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-body p-4">
                <form onSubmit={handleSave}>
                  <div className="row g-4">
                    <div className="col-12">
                      <label className="form-label fw-bold">Role Title <span className="text-danger">*</span></label>
                      <div className="input-group">
                        <span className="input-group-text bg-light"><Users size={16} /></span>
                        <input 
                            type="text" 
                            name="role"
                            className="form-control" 
                            placeholder="e.g., Senior Legal Counsel"
                            value={formData.role}
                            onChange={handleChange}
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-bold">Approval Limit <span className="text-danger">*</span></label>
                      <div className="input-group">
                        <span className="input-group-text bg-light"><DollarSign size={16} /></span>
                        <input 
                          type="number" 
                          name="approvalLimit"
                          className="form-control" 
                          placeholder="e.g., 50000"
                          value={formData.approvalLimit}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                        <label className="form-label fw-bold">Currency</label>
                        <select className="form-select" name="currency" value={formData.currency} onChange={handleChange}>
                            <option value="USD">USD - US Dollar</option>
                            <option value="EUR">EUR - Euro</option>
                            <option value="GBP">GBP - British Pound</option>
                            <option value="INR">INR - Indian Rupee</option>
                        </select>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-bold">Hierarchy Rank <span className="text-danger">*</span></label>
                      <div className="input-group">
                        <span className="input-group-text bg-light"><ArrowUp size={16} /></span>
                        <input 
                          type="number" 
                          name="rank"
                          className="form-control" 
                          placeholder="e.g., 1"
                          value={formData.rank}
                          onChange={handleChange}
                          min="1"
                        />
                      </div>
                      <div className="form-text">Higher number = Higher authority (e.g. 1=Manager, 5=CEO)</div>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-bold">Status</label>
                      <div className="form-check form-switch mt-2">
                        <input 
                          className="form-check-input" 
                          type="checkbox" 
                          name="isActive"
                          checked={formData.isActive}
                          onChange={handleChange}
                          id="doaStatus"
                        />
                        <label className="form-check-label" htmlFor="doaStatus">
                          {formData.isActive ? 'Active' : 'Inactive'}
                        </label>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card bg-light border-0 shadow-sm mb-4">
              <div className="card-body">
                <h6 className="fw-bold d-flex align-items-center gap-2 mb-3">
                  <Info size={18} className="text-primary" />
                  DoA Helper
                </h6>
                <p className="small text-secondary">
                  <strong>Role:</strong> The exact job title that matches your user directory.
                </p>
                <p className="small text-secondary">
                  <strong>Rank:</strong> Used to determine the approval chain order. Lower ranks approve first.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default CreateDoA;